import { type ReactNode, useMemo } from "react";

import { useOrganizationLookup } from "@mountayaapp/api";
import { ThemeProvider, toBranding } from "@mountayaapp/theme";

import { env } from "./env";

/**
 * BrandedThemeProviderProps carry only the app subtree to tint; the brand source is
 * fixed (the root organization), not passed in.
 */
interface BrandedThemeProviderProps {
  children: ReactNode;
}

/**
 * BrandedThemeProvider tints the whole web app with the ROOT organization's brand
 * palette, so the platform surface carries ONE consistent brand on every page — the
 * studio, the IAM/account settings, the auth screens — never the viewer's active org.
 * Per-org branding is applied only on the public embed, where each publisher's embedded
 * studio adopts its OWN org's colors (resolved on the embed route, not here).
 *
 * The root org's theme is read through its publishable-key lookup — a PUBLIC endpoint,
 * the same seam useStudioConfig reads the root default_props through — so it resolves for
 * any viewer regardless of which org they belong to (a session read of the root org would
 * 403 a non-member, and the lookup also works before auth resolves). Until it resolves, or
 * when no root key is configured, the app keeps the theme's default blue. The scheme stays
 * pinned light to match the app shell — only the primary palette is root-org-driven.
 */
export const BrandedThemeProvider = ({ children }: BrandedThemeProviderProps) => {
  const publishableKey = env.rootPublishableKey ?? "";
  const { data: organization } = useOrganizationLookup(
    { auth: { kind: "embed", publishableKey }, baseUrl: env.apiBaseUrl },
    publishableKey,
  );

  // Memoize on the palette reference (stable across renders from the query cache) so the
  // theme only rebuilds when the root org's brand color actually changes.
  const ui = organization?.settings.theme?.colors?.ui;
  const branding = useMemo(() => toBranding(ui), [ui]);

  return (
    <ThemeProvider branding={branding} colorScheme="light">
      {children}
    </ThemeProvider>
  );
};
